<template>
  <div class="mobile-betting-section">
    <div class="mobile-betting-header">
      <h2 class="mobile-section-title">2. Select Your Market</h2>
    </div>
    
    <!-- Tab Navigation -->
    <div class="mobile-tab-navigation">
      <button
        class="mobile-tab-btn"
        :class="{ active: activeTab === 'main' }"
        @click="activeTab = 'main'"
      >
        Main 🎯
      </button>
      <button
        class="mobile-tab-btn"
        :class="{ active: activeTab === 'bonus' }"
        @click="activeTab = 'bonus'"
      >
        Bonus Market 🎁
      </button>
    </div>
    
    <!-- Main Tab Content -->
    <div v-if="activeTab === 'main'" class="mobile-tab-content active-tab-content">
      <div class="mobile-market-dropdown-section">
        <div class="mobile-dropdown-container">
          <label class="mobile-dropdown-label">Select Market:</label>
          <select 
            class="mobile-market-dropdown"
            v-model="selectedMainMarket"
            @change="onMainMarketChange"
          >
            <option 
              v-for="market in allMainMarkets" 
              :key="market.id"
              :value="market.id"
            >
              {{ formatMarketDescription(market.description, market.outcomes[0]?.specifier) }}
            </option>
          </select>
        </div>
      </div>
      
      <!-- Number Selection Section -->
      <div v-if="selectedMainMarketData && selectedMainMarketData.outcomes.some(outcome => outcome.select_numbers)" class="mobile-number-selection-section">
        <div class="mobile-number-selection-header">
          <span class="mobile-number-selection-title">Select Numbers</span>
          <span v-if="currentOdds && selectedNumbers.length > 0" class="mobile-number-selection-odds">Odds: {{ formatOdds(currentOdds) }}</span>
        </div>

        <!-- Main Number Grid -->
        <div v-if="!isBonusOnlyMarket && !isMainIncludingBonusMarket" class="mobile-number-grid">
          <div
            v-for="n in actualBallsMax"
            :key="n"
            class="mobile-number-circle"
            :class="{
              selected: selectedNumbers.includes(n),
              hot: hotNumbers?.includes(n),
              cold: coldNumbers?.includes(n),
              disabled: (isSameDrumMarket) && selectedBonusNumbers.includes(n),
              'quick-pick': selectedNumbers.includes(n) && selectedNumbers.length === actualBalls,
            }"
            @click="toggleNumber(n)"
          >
            {{ n }}
          </div>
        </div>

        <!-- Main Including Bonus Number Grid (Single Grid) -->
        <div v-if="isMainIncludingBonusMarket" class="mobile-number-grid">
          <div
            v-for="n in actualBallsMax"
            :key="n"
            class="mobile-number-circle"
            :class="{
              selected: selectedNumbers.includes(n),
              hot: hotNumbers?.includes(n),
              cold: coldNumbers?.includes(n),
              'quick-pick': selectedNumbers.includes(n) && selectedNumbers.length >= 1,
            }"
            @click="toggleMainIncludingBonusNumber(n)"
          >
            {{ n }}
          </div>
        </div>

        <!-- Bonus Ball Selection -->
        <div v-if="(requiresBonusBall || isSameDrumMarket || isBonusOnlyMarket) && !isMainIncludingBonusMarket" class="mobile-bonus-section">
          <div class="mobile-bonus-title">
            {{ isSameDrumBonusMarket ? '' : (isBonusOnlyMarket ? 'SELECT BONUS NUMBER' : 'BONUS NUMBER') }}
          </div>
          <div v-if="!isSameDrumBonusMarket || isBonusOnlyMarket" class="mobile-bonus-number-grid">
            <div
              v-for="n in bonusBallsMax"
              :key="`bonus-${n}`"
              class="mobile-number-circle mobile-bonus-circle"
              :class="{
                selected: selectedBonusNumbers.includes(n),
                hot: hotNumbers?.includes(n),
                cold: coldNumbers?.includes(n),
                disabled: (isSameDrumMarket) && selectedNumbers.includes(n),
              }"
              @click="toggleBonusNumber(n)"
            >
              {{ n }}
            </div>
          </div>
        </div>

        <!-- Selected Market Info -->
        <div class="mobile-market-info">
          <div class="mobile-market-display" :class="{ 'center-layout': !((isBonusOnlyMarket && selectedBonusNumbers.length > 0) || (!isBonusOnlyMarket && selectedNumbers.length > 0)) }">
            <span class="mobile-market-text">
              <template v-if="isMainIncludingBonusMarket">
                <template v-if="selectedNumbers.length === 0">
                  Please Select Numbers
                </template>
                <template v-else>
                  {{ selectedNumbers.length }} Selected Ball{{ selectedNumbers.length !== 1 ? 's' : '' }}
                </template>
              </template>
              <template v-else-if="(isBonusOnlyMarket && selectedBonusNumbers.length > 0) || (!isBonusOnlyMarket && selectedNumbers.length > 0)">
                {{ isBonusOnlyMarket 
                  ? `${selectedBonusNumbers.length} Selected Bonus Ball${selectedBonusNumbers.length !== 1 ? 's' : ''}`
                  : `${selectedNumbers.length} Selected Ball${selectedNumbers.length !== 1 ? 's' : ''}`
                }}
              </template>
              <template v-else>
                Please Select a Number
              </template>
            </span>
            <span v-if="selectedNumbers.length > 0" class="mobile-market-odds">{{ formatOdds(currentOdds) }}</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mobile-action-buttons">
          <div class="mobile-quick-addline-row">
            <button
              class="mobile-quick-pick-btn"
              @click="quickPick"
            >
              Quick Pick
            </button>
            <button
              class="mobile-add-line-btn"
              @click="addLine"
              :disabled="!canAddLine"
            >
              Add Line
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Bonus Market Tab Content -->
    <div v-if="activeTab === 'bonus'" class="mobile-tab-content active-tab-content">
      <div class="mobile-bonus-markets">
        <div
          v-for="group in bonusMarkets"
          :key="group.name"
          class="mobile-bonus-group"
        >
          <div class="mobile-bonus-group-title">{{ formatMarketDescription(group.markets[0]?.description || '', group.markets[0]?.outcomes?.[0]?.specifier) || group.name }}</div>
          
          <div class="mobile-bonus-options">
            <template v-for="market in group.markets">
              <template v-if="market.outcomes && market.outcomes.length > 0">
                <div
                  v-for="outcome in market.outcomes"
                  :key="outcome.id"
                  class="mobile-bonus-bet-card"
                  :class="{ 'selected': isMarketSelected(market, outcome) }"
                  @click="selectBonusMarket(market, outcome, group)"
                >
                  <div class="mobile-bonus-bet-content">
                    <div class="mobile-bonus-bet-label">{{ formatOutcomeText(outcome) }}</div>
                    <div class="mobile-bonus-bet-odds">{{ formatOdds(Number(outcome.odds)) }}</div>
                  </div>
                  <div class="mobile-bonus-bet-indicator">
                    <div class="mobile-bonus-bet-check">✓</div>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch } from 'vue';
import { formatOdds } from '../../utils/currency';

interface MarketOutcome {
  id: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: Array<{
    id: number;
    name: string;
    balls_min: number;
    balls_max: number;
    picks_min: number;
    picks_max: number;
    has_bonus: number | boolean;
    max_resulted: number;
    type: string;
  }>;
}

interface SelectedMarket {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}

interface Props {
  bettingMarkets: MarketGroup[];
  selectedMarket: SelectedMarket | null;
  hotNumbers?: number[];
  coldNumbers?: number[];
  betslipLength: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'select-market': [market: SelectedMarket | null];
  'remove-market': [market: SelectedMarket];
  'toggle-group': [index: number];
  'tab-changed': [tab: 'main' | 'bonus'];
  'add-line': [line: { numbers: number[]; bonus?: number; numBets: number; picks: number }];
  'remove-line': [index: number];
  'add-to-betslip': [lines: Array<{ numbers: number[]; bonus?: number; numBets: number; picks: number }>];
  'clear-all': [];
}>();

// Tab state
const activeTab = ref<'main' | 'bonus'>('main');

// Main market selection
const selectedMainMarket = ref<number | ''>('');
const selectedBonusMarkets = ref<Array<{ market: Market; outcome: MarketOutcome }>>([]);

// Number selection state
const selectedNumbers = ref<number[]>([]);
const selectedBonusNumbers = ref<number[]>([]);
const lines = ref<Array<{ numbers: number[]; bonus?: number; numBets: number; picks: number }>>([]);

// Quick pick pattern memory
const lastQuickPickPattern = ref<{ mainCount: number; bonusCount: number } | null>(null);

// Computed properties to separate markets
const mainMarkets = computed(() => {
  return props.bettingMarkets.filter(group => 
    group.markets.some(market => 
      market.outcomes.some(outcome => outcome.select_numbers)
    )
  );
});

const bonusMarkets = computed(() => {
  return props.bettingMarkets.map(group => ({
    ...group,
    markets: group.markets.filter(market => 
      market.outcomes.every(outcome => !outcome.select_numbers)
    )
  })).filter(group => group.markets.length > 0);
});

// Flatten all main markets for dropdown
const allMainMarkets = computed(() => {
  const markets: Market[] = [];
  mainMarkets.value.forEach(group => {
    group.markets.forEach(market => {
      if (market.outcomes.some(outcome => outcome.select_numbers)) {
        markets.push(market);
      }
    });
  });
  return markets.sort((a, b) => {
    const isABonusBallSameDrum = a.slug === 'bonus-ball-bet-same-drum-as-standard-numbers';
    const isBBonusBallSameDrum = b.slug === 'bonus-ball-bet-same-drum-as-standard-numbers';
    if (isABonusBallSameDrum && !isBBonusBallSameDrum) return 1;
    if (!isABonusBallSameDrum && isBBonusBallSameDrum) return -1;
    return 0;
  });
});

// Get selected market data
const selectedMainMarketData = computed(() => {
  if (!selectedMainMarket.value) return null;
  return allMainMarkets.value.find(market => market.id === selectedMainMarket.value);
});

// Get market group for selected market
const selectedMainMarketGroup = computed(() => {
  if (!selectedMainMarket.value) return null;
  return mainMarkets.value.find(group => 
    group.markets.some(market => market.id === selectedMainMarket.value)
  );
});

// Number selection computed properties
const actualBallsMax = computed(() => {
  if (selectedMainMarketGroup.value?.rules?.length) {
    const mainRule = selectedMainMarketGroup.value.rules.find(rule => rule.type === 'main' || !rule.has_bonus);
    return mainRule ? mainRule.balls_max : 49;
  }
  return 49; // Default
});

const actualBalls = computed(() => {
  if (selectedMainMarketData.value?.outcomes) {
    const firstOutcome = selectedMainMarketData.value.outcomes.find(outcome => outcome.select_numbers);
    return firstOutcome ? firstOutcome.pick : 5;
  }
  return 5; // Default
});

const bonusBallsMax = computed(() => {
  if (isSameDrumMarket.value) {
    return actualBallsMax.value;
  }
  if (selectedMainMarketGroup.value?.rules?.length) {
    const bonusRule = selectedMainMarketGroup.value.rules.find(rule => rule.has_bonus === 1);
    return bonusRule ? bonusRule.balls_max : 20;
  }
  return 20; // Default
});

const maxSelectableBalls = computed(() => {
  if (selectedMainMarketData.value?.outcomes) {
    const maxPick = Math.max(...selectedMainMarketData.value.outcomes
      .filter(outcome => outcome.select_numbers)
      .map(outcome => outcome.pick)
    );
    return maxPick || actualBallsMax.value;
  }
  return actualBallsMax.value;
});

const requiresBonusBall = computed(() => {
  return selectedMainMarketGroup.value?.has_bonus || false;
});

const isBonusOnlyMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'bonus-ball-only-same-drum' || 
         slug === 'bonus-ball-only-additional-drum';
});

const isSameDrumMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'standard-bet-and-bonus-ball-same-drum' ||
         slug === 'bonus-ball-only-same-drum' ||
         slug === 'standard-bet-bonus-from-same-drum' ||
         slug === 'regular-numbers-plus-bonus-ball-same-drum';
});

const isMainIncludingBonusMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'standard-bet-bonus-from-same-drum';
});

const isSameDrumBonusMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'bonus-ball-only-same-drum' ||
         slug === 'standard-bet-bonus-from-same-drum';
});

const canAddLine = computed(() => {
  if (isBonusOnlyMarket.value) {
    return selectedBonusNumbers.value.length > 0;
  }
  
  // For main including bonus markets, need at least 1 number (1-6 balls allowed)
  if (isMainIncludingBonusMarket.value) {
    return selectedNumbers.value.length >= 1;
  }
  
  if (isSameDrumMarket.value && !isMainIncludingBonusMarket.value) {
    const hasDuplicates = selectedNumbers.value.some(mainNum => 
      selectedBonusNumbers.value.includes(mainNum)
    );
    if (hasDuplicates) {
      return false;
    }
    
    return selectedNumbers.value.length > 0 && selectedBonusNumbers.value.length > 0;
  }
  
  if (requiresBonusBall.value && !isSameDrumMarket.value) {
    return selectedNumbers.value.length > 0 && selectedBonusNumbers.value.length > 0;
  }
  return selectedNumbers.value.length > 0;
});

// Computed properties for current odds display
const currentOutcome = computed(() => {
  if (!selectedMainMarketData.value || selectedNumbers.value.length === 0) return null;
  
  // For main including bonus markets, use the actual number count
  if (isMainIncludingBonusMarket.value) {
    return selectedMainMarketData.value.outcomes.find(outcome => 
      outcome.select_numbers && outcome.pick === selectedNumbers.value.length
    );
  }
  
  return selectedMainMarketData.value.outcomes.find(outcome => 
    outcome.select_numbers && outcome.pick === selectedNumbers.value.length
  );
});

const currentOdds = computed(() => {
  return currentOutcome.value ? Number(currentOutcome.value.odds) : null;
});


// Set default market selection
const formatOutcomeText = (outcome: MarketOutcome): string => {
  let text = outcome.text || outcome.id.toString();
  
  // Replace {value} placeholder with specifier value
  if (text.includes('{value}') && outcome.specifier) {
    text = text.replace('{value}', outcome.specifier);
  }
  
  return text;
};

const onMainMarketChange = () => {
  // Clear all number selections when market changes
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  lines.value = [];
  
  if (selectedMainMarket.value && selectedMainMarketData.value && selectedMainMarketGroup.value) {
    // Find the first outcome with select_numbers for this market
    const firstOutcome = selectedMainMarketData.value.outcomes.find(outcome => outcome.select_numbers);
    if (firstOutcome) {
      const marketData: SelectedMarket = {
        marketGroup: selectedMainMarketGroup.value,
        market: selectedMainMarketData.value,
        label: formatOutcomeText(firstOutcome),
        odds: Number(firstOutcome.odds),
        select_numbers: firstOutcome.select_numbers,
        balls: firstOutcome.pick,
        outcome_id: firstOutcome.id.toString()
      };
      emit('select-market', marketData);
    }
  }
};

const setDefaultMainMarket = () => {
  if (allMainMarkets.value.length > 0 && !selectedMainMarket.value) {
    selectedMainMarket.value = allMainMarkets.value[0].id;
    onMainMarketChange();
  }
};

// Watch for changes in allMainMarkets to set default when data is available
watch(allMainMarkets, (newMarkets) => {
  if (newMarkets.length > 0 && !selectedMainMarket.value) {
    setDefaultMainMarket();
  }
}, { immediate: true });

// Watch for tab changes to clear main market selection when switching to bonus
watch(activeTab, (newTab) => {
  emit('tab-changed', newTab);
  if (newTab === 'bonus') {
    selectedMainMarket.value = '';
  } else if (newTab === 'main' && !selectedMainMarket.value) {
    setDefaultMainMarket();
  }
});

// Initialize default selection
setDefaultMainMarket();

const selectBonusMarket = (market: Market, outcome: MarketOutcome, group: MarketGroup) => {
  // Check if this market/outcome is already selected
  const isAlreadySelected = selectedBonusMarkets.value.some(selected => 
    selected.market.id === market.id &&
    selected.outcome.id === outcome.id
  );
  
  if (isAlreadySelected) {
    // Remove from selection (toggle off)
    selectedBonusMarkets.value = selectedBonusMarkets.value.filter(selected => 
      !(selected.market.id === market.id &&
        selected.outcome.id === outcome.id)
    );
    
    // Emit remove event to parent
    emit('remove-market', {
      marketGroup: group,
      market: market,
      label: formatOutcomeText(outcome),
      odds: Number(outcome.odds),
      select_numbers: outcome.select_numbers,
      balls: outcome.pick,
      outcome_id: outcome.id.toString()
    });
  } else {
    // Add to selection (toggle on)
    selectedBonusMarkets.value.push({ market, outcome });
    
    // Emit add event to parent
    emit('select-market', {
      marketGroup: group,
      market: market,
      label: formatOutcomeText(outcome),
      odds: Number(outcome.odds),
      select_numbers: outcome.select_numbers,
      balls: outcome.pick,
      outcome_id: outcome.id.toString()
    });
  }
};

// Removed toggleBonusGroup - bonus markets are now always visible

const isMarketSelected = (market: Market, outcome: MarketOutcome): boolean => {
  // Check if this is a bonus market selection
  if (selectedBonusMarkets.value.length > 0) {
    return selectedBonusMarkets.value.some(selected => 
      selected.market.id === market.id &&
      selected.outcome.id === outcome.id
    );
  }
  
  // Check if this is a main market selection
  if (!props.selectedMarket) return false;
  return (
    props.selectedMarket.market.id === market.id &&
    props.selectedMarket.outcome_id === outcome.id.toString()
  );
};

const formatMarketDescription = (description: string, specifier?: string | null): string => {
  if (!description) return '';
  
  // Replace {value} placeholder with specifier value
  if (description.includes('{value}') && specifier) {
    description = description.replace('{value}', specifier);
  }
  
  return description;
};


// Number selection methods
const toggleMainIncludingBonusNumber = (n: number) => {
  const index = selectedNumbers.value.indexOf(n);
  
  if (index === -1) {
    // Add number if we have space (max 6 main numbers)
    if (selectedNumbers.value.length < 6) {
      selectedNumbers.value.push(n);
    }
  } else {
    // Remove number and shift remaining numbers
    selectedNumbers.value.splice(index, 1);
  }
  
  // Update market selection with current number count
  updateSelectedMarketForBalls(selectedNumbers.value.length);
};

const updateSelectedMarketForBalls = (numberOfBalls: number) => {
  if (!selectedMainMarketData.value) return;
  
  // Find the outcome that matches the number of balls selected
  const matchingOutcome = selectedMainMarketData.value.outcomes.find(outcome => 
    outcome.select_numbers && outcome.pick === numberOfBalls
  );
  
  if (matchingOutcome && selectedMainMarketGroup.value) {
    const marketData: SelectedMarket = {
      marketGroup: selectedMainMarketGroup.value,
      market: selectedMainMarketData.value,
      label: formatOutcomeText(matchingOutcome),
      odds: Number(matchingOutcome.odds),
      select_numbers: matchingOutcome.select_numbers,
      balls: matchingOutcome.pick,
      outcome_id: matchingOutcome.id.toString()
    };
    emit('select-market', marketData);
  }
};

const toggleNumber = (n: number) => {
  if (isSameDrumMarket.value && selectedBonusNumbers.value.includes(n)) {
    return;
  }
  
  if (selectedNumbers.value.includes(n)) {
    selectedNumbers.value = selectedNumbers.value.filter(num => num !== n);
  } else if (selectedNumbers.value.length < maxSelectableBalls.value) {
    selectedNumbers.value.push(n);
  }
  
  // Update market selection with current number count
  updateSelectedMarketForBalls(selectedNumbers.value.length);
};

const toggleBonusNumber = (n: number) => {
  if (isSameDrumMarket.value && selectedNumbers.value.includes(n)) {
    return;
  }
  
  if (selectedBonusNumbers.value.includes(n)) {
    selectedBonusNumbers.value = selectedBonusNumbers.value.filter(num => num !== n);
  } else {
    selectedBonusNumbers.value = [n]; // Only one bonus number allowed
  }
};

const quickPick = () => {
  if (isBonusOnlyMarket.value) {
    // For bonus-only markets, maintain the same number of bonus selections
    const currentBonusCount = selectedBonusNumbers.value.length;
    const targetBonusCount = currentBonusCount > 0 ? currentBonusCount : 1;
    
    const bonusNumbers = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
    const shuffledBonus = bonusNumbers.sort(() => Math.random() - 0.5);
    selectedBonusNumbers.value = shuffledBonus.slice(0, targetBonusCount);
    
    // Store pattern for bonus-only market
    lastQuickPickPattern.value = { mainCount: 0, bonusCount: targetBonusCount };
  } else {
    // For regular markets, 
    let mainCount: number;
    let bonusCount: number;
    
    // If user has existing selections, maintain the same count but clear previous selections
    if (selectedNumbers.value.length > 0 || selectedBonusNumbers.value.length > 0) {
      mainCount = selectedNumbers.value.length > 0 ? selectedNumbers.value.length : actualBalls.value;
      bonusCount = selectedBonusNumbers.value.length > 0 ? selectedBonusNumbers.value.length : 
                   ((requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0);
      
      // Clear previous selections before picking new ones
      selectedNumbers.value = [];
      selectedBonusNumbers.value = [];
    } else if (lastQuickPickPattern.value) {
      // Use the same pattern as the last quick pick
      mainCount = lastQuickPickPattern.value.mainCount;
      bonusCount = lastQuickPickPattern.value.bonusCount;
    } else {
      // First time - use current market requirements
      mainCount = actualBalls.value;
      bonusCount = (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0;
    }
    
    // Quick pick main numbers
    if (mainCount > 0) {
      const availableNumbers = Array.from({ length: actualBallsMax.value }, (_, i) => i + 1);
      const shuffled = availableNumbers.sort(() => Math.random() - 0.5);
      selectedNumbers.value = shuffled.slice(0, mainCount);
    }
    
    // Quick pick bonus numbers (if required)
    if (bonusCount > 0) {
      const bonusNumbers = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
      const availableBonusNumbers = isSameDrumMarket.value 
        ? bonusNumbers.filter(num => !selectedNumbers.value.includes(num))
        : bonusNumbers;
      
      if (availableBonusNumbers.length > 0) {
        const shuffledBonus = availableBonusNumbers.sort(() => Math.random() - 0.5);
        selectedBonusNumbers.value = shuffledBonus.slice(0, bonusCount);
      } else {
        selectedBonusNumbers.value = [];
      }
    }
    
    // Store the pattern for next quick pick
    lastQuickPickPattern.value = { mainCount, bonusCount };
    
    // Update market selection to match the number of balls selected
    updateSelectedMarketForBalls(selectedNumbers.value.length);
  }
};

const addLine = () => {
  if (!canAddLine.value) return;
  
  const line = {
    numbers: [...selectedNumbers.value],
    bonus: (requiresBonusBall.value || (isSameDrumMarket.value && !isMainIncludingBonusMarket.value) || isBonusOnlyMarket.value) ? selectedBonusNumbers.value[0] : undefined,
    numBets: 1,
    picks: selectedNumbers.value.length
  };
  
  lines.value.push(line);
  
  // Store the pattern for future quick picks
  lastQuickPickPattern.value = {
    mainCount: selectedNumbers.value.length,
    bonusCount: selectedBonusNumbers.value.length
  };
  
  // Emit the line to parent
  emit('add-line', line);
  
  // Clear selections for next line
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const removeLine = (index: number) => {
  lines.value.splice(index, 1);
  emit('remove-line', index);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clearAll = () => {
  lines.value = [];
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  emit('clear-all');
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const addToBetslip = () => {
  emit('add-to-betslip', lines.value);
};

// Reset quick pick pattern when market changes
watch(selectedMainMarket, () => {
  lastQuickPickPattern.value = null;
});

watch(() => props.betslipLength, (newLength, oldLength) => {
  if (oldLength && oldLength > 0 && newLength === 0) {
    selectedBonusMarkets.value = [];
  }
});

// Reset internal state when market is cleared
watch(() => props.selectedMarket, (newMarket) => {
  if (!newMarket) {
    // Reset all internal state when market is cleared
    selectedNumbers.value = [];
    selectedBonusNumbers.value = [];
    lines.value = [];
    lastQuickPickPattern.value = null;
  }
});
</script>

<script lang="ts">
export default {
  name: "MobileBettingSection",
};
</script>

<style scoped>
.mobile-betting-section {
  background: var(--bg-secondary);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
}

.mobile-betting-header {
  padding: 16px 20px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.mobile-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.mobile-tab-navigation {
  display: flex;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.mobile-tab-btn {
  flex: 1;
  padding: 12px 16px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
}

.mobile-tab-btn:hover {
  color: var(--text-primary);
  background: var(--bg-hover);
}

.mobile-tab-btn.active {
  color: var(--accent-color);
  border-bottom-color: var(--accent-color);
  background: var(--bg-primary);
  font-weight: 700;
}

.mobile-tab-content {
  padding: 16px 20px;
}

.active-tab-content {
  border-radius: 12px;
  background: var(--bg-card);
  box-shadow: 0 4px 12px rgba(250, 96, 0, 0.1);
}

.mobile-market-dropdown-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-dropdown-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-dropdown-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.mobile-market-dropdown {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-market-dropdown:hover {
  border-color: var(--accent-color);
}

.mobile-market-dropdown:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 2px rgba(var(--accent-color-rgb), 0.2);
}

.mobile-selected-market-info {
  background: var(--bg-tertiary);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid var(--border-color);
}

.mobile-market-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.mobile-market-description {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.mobile-betting-group {
  margin-bottom: 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg-primary);
}

.mobile-betting-group:last-child {
  margin-bottom: 0;
}

.mobile-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: var(--bg-tertiary);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.mobile-group-header:hover {
  background: var(--bg-hover);
}

.mobile-group-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.mobile-expand-btn {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.mobile-expand-btn:hover {
  background: var(--bg-hover);
}

.mobile-expand-btn img {
  width: 16px;
  height: 16px;
  filter: var(--icon-filter);
}

.mobile-group-content {
  padding: 16px;
}

.mobile-market-item {
  margin-bottom: 16px;
}

.mobile-market-item:last-child {
  margin-bottom: 0;
}

.mobile-market-header {
  margin-bottom: 12px;
}

.mobile-market-description {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.mobile-betting-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-bet-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-bet-row:hover {
  background: var(--bg-hover);
  border-color: var(--accent-color);
}

.mobile-bet-row.selected {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.mobile-bet-row.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-bet-row.disabled:hover {
  background: var(--bg-secondary);
  border-color: var(--border-color);
}

.mobile-bet-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.mobile-bet-row.selected .mobile-bet-label {
  color: white;
}

.mobile-bet-amount {
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-color);
}

.mobile-bet-row.selected .mobile-bet-amount {
  color: white;
}

/* New optimized bonus market styles */
.mobile-bonus-markets {
  padding: 12px;
}

.mobile-bonus-group {
  margin-bottom: 20px;
}

.mobile-bonus-group:last-child {
  margin-bottom: 0;
}

.mobile-bonus-group-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.mobile-bonus-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.mobile-bonus-bet-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 60px;
}

.mobile-bonus-bet-card:hover {
  border-color: var(--accent-color);
  background: var(--bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.mobile-bonus-bet-card.selected {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(var(--accent-color-rgb), 0.3);
}

.mobile-bonus-bet-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  /* gap: 4px; */
}

.mobile-bonus-bet-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
}

.mobile-bonus-bet-card.selected .mobile-bonus-bet-label {
  color: white;
}

.mobile-bonus-bet-odds {
  font-size: 16px;
  font-weight: 700;
  color: var(--accent-color);
}

.mobile-bonus-bet-card.selected .mobile-bonus-bet-odds {
  color: white;
}

.mobile-bonus-bet-indicator {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--bg-tertiary);
  border: 2px solid var(--border-color);
  transition: all 0.2s ease;
}

.mobile-bonus-bet-card.selected .mobile-bonus-bet-indicator {
  background: white;
  border-color: white;
}

.mobile-bonus-bet-check {
  font-size: 14px;
  font-weight: bold;
  color: var(--text-secondary);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.mobile-bonus-bet-card.selected .mobile-bonus-bet-check {
  color: var(--accent-color);
  opacity: 1;
}

/* Number Selection Section */
.mobile-number-selection-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
}

.mobile-number-selection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.mobile-number-selection-title {
  flex: 1;
}

.mobile-number-selection-odds {
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-color);
}

.mobile-number-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
  margin-bottom: 16px;
}

@media (min-width: 375px) {
  .mobile-number-grid {
    grid-template-columns: repeat(7, 1fr);
  }
}

@media (max-width: 320px) {
  .mobile-number-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

.mobile-number-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
}

@media (max-width: 320px) {
  .mobile-number-circle {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
}

.mobile-number-circle:hover {
  border-color: var(--accent-color);
  background: var(--bg-hover);
}

.mobile-number-circle.selected {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.mobile-number-circle.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.mobile-number-circle.quick-pick {
  animation: pulse 1s infinite;
}

.mobile-number-circle.main-number {
  background: var(--accent-color);
  color: white;
}

.mobile-number-circle.bonus-number {
  background: var(--warning-color, #f59e0b);
  color: white;
}

.number-type-indicator {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 10px;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.mobile-bonus-section {
  margin-bottom: 16px;
}

.mobile-bonus-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
  text-align: center;
}

.mobile-bonus-number-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
}

@media (min-width: 375px) {
  .mobile-bonus-number-grid {
    grid-template-columns: repeat(7, 1fr);
  }
}

@media (max-width: 320px) {
  .mobile-bonus-number-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

.mobile-bonus-circle {
  background: var(--bg-primary);
  border-color: var(--border-color);
}

.mobile-bonus-circle.selected {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

/* Market Info Display */
.mobile-market-info {
  margin: 16px 0;
  display: flex;
  justify-content: center;
}

.mobile-market-display {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  min-width: 200px;
}

.mobile-market-display.center-layout {
  justify-content: center;
}

.mobile-market-text {
  font-size: 14px;
  font-weight: 400;
  color: var(--text-primary);
}

.mobile-market-odds {
  font-size: 16px;
  font-weight: 700;
  color: var(--accent-color);
}

.mobile-action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-quick-addline-row {
  display: flex;
  gap: 8px;
}

.mobile-quick-pick-btn,
.mobile-add-line-btn {
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-quick-pick-btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.mobile-quick-pick-btn:hover:not(:disabled) {
  background: var(--bg-hover);
}

.mobile-quick-pick-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-add-line-btn {
  background: var(--accent-color);
  color: white;
}

.mobile-add-line-btn:hover:not(:disabled) {
  background: var(--accent-color-dark, #e04500);
}

.mobile-add-line-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .mobile-betting-section {
    background: var(--bg-secondary);
  }
  
  .mobile-betting-header {
    background: var(--bg-tertiary);
  }
  
  .mobile-betting-group {
    background: var(--bg-primary);
  }
  
  .mobile-group-header {
    background: var(--bg-tertiary);
  }
  
  .mobile-bet-row {
    background: var(--bg-secondary);
  }
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .mobile-betting-header {
    padding: 12px 16px;
  }
  
  .mobile-section-title {
    font-size: 14px;
  }
  
  .mobile-betting-groups {
    padding: 12px 16px;
  }
  
  .mobile-group-header {
    padding: 12px;
  }
  
  .mobile-group-title {
    font-size: 13px;
  }
  
  .mobile-group-content {
    padding: 12px;
  }
  
  .mobile-bet-row {
    padding: 10px 12px;
  }
  
  .mobile-bet-label {
    font-size: 13px;
  }
  
  .mobile-bet-amount {
    font-size: 13px;
  }
  
  /* Bonus market responsive styles */
  .mobile-bonus-markets {
    padding: 0px;
  }
  
  .mobile-bonus-options {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
  
  .mobile-bonus-bet-card {
    padding: 10px 12px;
    min-height: 30px;
  }
  
  .mobile-bonus-bet-label {
    font-size: 12px;
  }
  
  .mobile-bonus-bet-odds {
    font-size: 14px;
  }
  
  .mobile-bonus-bet-indicator {
    width: 20px;
    height: 20px;
  }
  
  .mobile-bonus-bet-check {
    font-size: 12px;
  }
  
  .mobile-tab-content {
    padding: 12px 8px;
  }
  
  .mobile-tab-btn {
    padding: 10px 12px;
    font-size: 13px;
  }
  
  .mobile-market-dropdown {
    padding: 10px 12px;
    font-size: 13px;
  }
  
  .mobile-dropdown-label {
    font-size: 13px;
  }
  
  .mobile-selected-market-info {
    padding: 12px;
  }
  
  .mobile-market-title {
    font-size: 14px;
  }
  
  .mobile-market-description {
    font-size: 13px;
  }
}
</style>
